import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader, getUser } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Eye, Edit, Trash2, Upload, Download, X } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const VendorManagement = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [vendors, setVendors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [sacCodes, setSacCodes] = useState([]);
  const [states, setStates] = useState([]);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [codesLoaded, setCodesLoaded] = useState(false);
  const [formData, setFormData] = useState({
    legal_entity_name: '',
    trade_name: '',
    registered_address: '',
    business_address: '',
    state: '',
    gst_registered: 'yes',
    gst_number: '',
    credit_period: '',
    hsn_codes: [],
    sac_codes: [],
    poc_name: '',
    poc_mobile: '',
    poc_email: ''
  });
  const [documents, setDocuments] = useState({
    cin: null,
    pan: null,
    gst: null,
    cheque: null
  });
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    // Check authorization
    const allowed_roles = ['requester', 'function_approver', 'finance_head', 'finance_team', 'accounts', 'admin'];
    if (!user || !allowed_roles.includes(user.role)) {
      toast.error('Not authorized to access vendors');
      navigate('/');
      return;
    }
    fetchVendors();
    fetchCodes();
  }, []); // Empty dependency array - only run once on mount

  const fetchVendors = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: getAuthHeader()
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle 429 (rate limit) error with retry
      if (error.response?.status === 429 && retryCount < 2) {
        console.log(`Rate limited, retrying in ${(retryCount + 1) * 1000}ms...`);
        setTimeout(() => {
          fetchVendors(retryCount + 1);
        }, (retryCount + 1) * 1000);
        return;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch vendors';
      toast.error(`Failed to fetch vendors: ${errorMsg}`);
    }
  };

  const fetchCodes = async () => {
    // Prevent duplicate calls
    if (codesLoaded) {
      return;
    }
    
    try {
      const [hsnRes, sacRes, statesRes] = await Promise.all([
        axios.get(`${API}/hsn-codes`, { headers: getAuthHeader() }),
        axios.get(`${API}/sac-codes`, { headers: getAuthHeader() }),
        axios.get(`${API}/states`, { headers: getAuthHeader() })
      ]);
      // Backend returns array directly, not wrapped in an object
      setHsnCodes(Array.isArray(hsnRes.data) ? hsnRes.data : []);
      setSacCodes(Array.isArray(sacRes.data) ? sacRes.data : []);
      setStates(Array.isArray(statesRes.data) ? statesRes.data : []);
      setCodesLoaded(true);
      console.log(`Loaded ${hsnRes.data?.length || 0} HSN codes, ${sacRes.data?.length || 0} SAC codes, and ${statesRes.data?.length || 0} states`);
    } catch (error) {
      console.error('Error fetching codes:', error);
      console.error('Error details:', error.response?.data);
      // Don't show toast for code fetching errors as it's not critical
      // Vendor creation/editing can still work without the code lists
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate documents are provided (only PAN is required)
    const requiredDocs = !documents.pan;
    const gstDocRequired = formData.gst_registered === 'yes' && !documents.gst;
    
    if (requiredDocs || gstDocRequired) {
      const missingDocs = [];
      if (!documents.pan) missingDocs.push('PAN Document');
      if (formData.gst_registered === 'yes' && !documents.gst) missingDocs.push('GST Certificate');
      toast.error(`Missing required documents: ${missingDocs.join(', ')}`);
      setLoading(false);
      return;
    }
    
    try {
      // Prepare vendor data with proper types
      const vendorData = {
        ...formData,
        credit_period: parseInt(formData.credit_period, 10) || 0,
        gst_number: formData.gst_registered === 'yes' ? formData.gst_number : null,
        poc_mobile: formData.poc_mobile && formData.poc_mobile.trim() !== '' ? formData.poc_mobile : null,
        poc_email: formData.poc_email && formData.poc_email.trim() !== '' ? formData.poc_email : null
      };
      
      console.log('Sending vendor data:', vendorData);
      
      const response = await axios.post(`${API}/vendors`, vendorData, {
        headers: getAuthHeader()
      });

      const vendorId = response.data.id;

      // Upload documents to correct endpoints
      const uploadPromises = [];
      
      if (documents.cin) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.cin);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-cin`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'cin',
            url: res.data.url
          })).catch(err => {
            console.error('CIN upload failed:', err);
            throw new Error('CIN document upload failed');
          })
        );
      }

      if (documents.pan) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.pan);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-pan`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'pan',
            url: res.data.url
          })).catch(err => {
            console.error('PAN upload failed:', err);
            throw new Error('PAN document upload failed');
          })
        );
      }

      if (documents.gst) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.gst);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-gst`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'gst',
            url: res.data.url
          })).catch(err => {
            console.error('GST upload failed:', err);
            throw new Error('GST certificate upload failed');
          })
        );
      }

      if (documents.cheque) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.cheque);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-cheque`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'cheque',
            url: res.data.url
          })).catch(err => {
            console.error('Cheque upload failed:', err);
            throw new Error('Cancelled cheque upload failed');
          })
        );
      }

      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises);
      
      // Update vendor with document URLs
      const updateData = {};
      uploadResults.forEach(result => {
        if (result) {
          if (result.type === 'cin') updateData.cin_document_url = result.url;
          if (result.type === 'pan') updateData.pan_document_url = result.url;
          if (result.type === 'gst') updateData.gst_certificate_url = result.url;
          if (result.type === 'cheque') updateData.cheque_document_url = result.url;
        }
      });

      if (Object.keys(updateData).length > 0) {
        // Prepare update data with proper types (same as vendorData)
        const vendorUpdateData = {
          ...formData,
          credit_period: parseInt(formData.credit_period, 10) || 0,
          gst_number: formData.gst_registered === 'yes' ? formData.gst_number : null,
          ...updateData
        };
        await axios.put(`${API}/vendors/${vendorId}`, vendorUpdateData, {
          headers: getAuthHeader()
        });
      }

      toast.success('Vendor created successfully');
      setDialogOpen(false);
      resetForm();
      
      // Add a small delay before fetching vendors to avoid rate limiting
      setTimeout(() => {
        fetchVendors();
      }, 500);
    } catch (error) {
      console.error('Vendor creation error:', error);
      
      // Handle different error types properly
      let errorMessage = 'Failed to create vendor';
      
      if (error.message && error.message.includes('upload failed')) {
        // Custom error from document upload
        errorMessage = error.message;
      } else if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle FastAPI validation errors
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    
    // Validate GST number if GST registered is "yes"
    if (formData.gst_registered === 'yes' && !formData.gst_number) {
      toast.error('GST Number is required when GST Registered is Yes');
      return;
    }
    
    // Validate required documents (only PAN is required)
    const missingDocs = [];
    
    // PAN document is always required (either existing or new upload)
    if (!selectedVendor.pan_document_url && !documents.pan) {
      missingDocs.push('PAN Document');
    }
    
    // GST certificate required if GST registered is "yes"
    if (formData.gst_registered === 'yes' && !selectedVendor.gst_certificate_url && !documents.gst) {
      missingDocs.push('GST Certificate');
    }
    
    if (missingDocs.length > 0) {
      toast.error(`Missing required documents: ${missingDocs.join(', ')}`);
      return;
    }
    
    setLoading(true);
    try {
      // Upload new documents if provided
      const uploadPromises = [];
      
      if (documents.cin) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.cin);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-cin`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'cin',
            url: res.data.url
          })).catch(err => {
            console.error('CIN upload failed:', err);
            toast.error('CIN document upload failed');
            return null;
          })
        );
      }

      if (documents.pan) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.pan);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-pan`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'pan',
            url: res.data.url
          })).catch(err => {
            console.error('PAN upload failed:', err);
            toast.error('PAN document upload failed');
            return null;
          })
        );
      }

      if (documents.gst) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.gst);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-gst`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'gst',
            url: res.data.url
          })).catch(err => {
            console.error('GST upload failed:', err);
            toast.error('GST certificate upload failed');
            return null;
          })
        );
      }

      if (documents.cheque) {
        const formDataFile = new FormData();
        formDataFile.append('file', documents.cheque);
        uploadPromises.push(
          axios.post(
            `${API}/vendors/upload-cheque`,
            formDataFile,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          ).then(res => ({
            type: 'cheque',
            url: res.data.url
          })).catch(err => {
            console.error('Cheque upload failed:', err);
            toast.error('Cancelled cheque upload failed');
            return null;
          })
        );
      }

      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises);
      
      // Merge document URLs with form data and ensure proper types
      const updateData = {
        ...formData,
        credit_period: parseInt(formData.credit_period, 10) || 0,
        gst_number: formData.gst_registered === 'yes' ? formData.gst_number : null,
        poc_mobile: formData.poc_mobile && formData.poc_mobile.trim() !== '' ? formData.poc_mobile : null,
        poc_email: formData.poc_email && formData.poc_email.trim() !== '' ? formData.poc_email : null
      };
      
      // Preserve existing document URLs if no new files are uploaded
      if (!documents.cin && selectedVendor.cin_document_url) {
        updateData.cin_document_url = selectedVendor.cin_document_url;
      }
      if (!documents.pan && selectedVendor.pan_document_url) {
        updateData.pan_document_url = selectedVendor.pan_document_url;
      }
      if (!documents.gst && selectedVendor.gst_certificate_url) {
        updateData.gst_certificate_url = selectedVendor.gst_certificate_url;
      }
      if (!documents.cheque && selectedVendor.cheque_document_url) {
        updateData.cheque_document_url = selectedVendor.cheque_document_url;
      }
      
      uploadResults.forEach(result => {
        if (result) {
          if (result.type === 'cin') updateData.cin_document_url = result.url;
          if (result.type === 'pan') updateData.pan_document_url = result.url;
          if (result.type === 'gst') updateData.gst_certificate_url = result.url;
          if (result.type === 'cheque') updateData.cheque_document_url = result.url;
        }
      });

      // Update vendor with all data
      await axios.put(`${API}/vendors/${selectedVendor.id}`, updateData, {
        headers: getAuthHeader()
      });

      toast.success('Vendor updated successfully');
      setEditDialogOpen(false);
      resetForm();
      
      // Add a small delay before fetching vendors to avoid rate limiting
      setTimeout(() => {
        fetchVendors();
      }, 500);
    } catch (error) {
      console.error('Vendor update error:', error);
      
      // Handle different error types properly
      let errorMessage = 'Failed to update vendor';
      
      if (error.message && error.message.includes('upload failed')) {
        // Custom error from document upload
        errorMessage = error.message;
      } else if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle FastAPI validation errors
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVendor = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/vendors/${vendorToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Vendor deleted successfully');
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
      
      // Add a small delay before fetching vendors to avoid rate limiting
      setTimeout(() => {
        fetchVendors();
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      legal_entity_name: '',
      trade_name: '',
      registered_address: '',
      business_address: '',
      state: '',
      gst_registered: 'yes',
      gst_number: '',
      credit_period: '',
      hsn_codes: [],
      sac_codes: [],
      poc_name: '',
      poc_mobile: '',
      poc_email: ''
    });
    setDocuments({ cin: null, pan: null, gst: null, cheque: null });
    setSelectedVendor(null);
  };

  const openDetailsDialog = (vendor) => {
    setSelectedVendor(vendor);
    setDetailsDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      legal_entity_name: vendor.legal_entity_name,
      trade_name: vendor.trade_name,
      registered_address: vendor.registered_address,
      business_address: vendor.business_address,
      state: vendor.state,
      gst_registered: vendor.gst_registered || 'yes',
      gst_number: vendor.gst_number || '',
      credit_period: vendor.credit_period || '',
      hsn_codes: vendor.hsn_codes || [],
      sac_codes: vendor.sac_codes || [],
      poc_name: vendor.poc_name,
      poc_mobile: vendor.poc_mobile,
      poc_email: vendor.poc_email
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (vendor) => {
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: 'Approved', color: 'bg-green-100 text-green-800' },
      'rejected': { label: 'Rejected', color: 'bg-red-100 text-red-800' },
      'active': { label: 'Active', color: 'bg-green-100 text-green-800' }
    };
    const config = statusConfig[status] || { label: status || 'Active', color: 'bg-green-100 text-green-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const addHsnCode = (code) => {
    if (code && !formData.hsn_codes.includes(code)) {
      setFormData({ ...formData, hsn_codes: [...formData.hsn_codes, code] });
    }
  };

  const removeHsnCode = (code) => {
    setFormData({ ...formData, hsn_codes: formData.hsn_codes.filter(c => c !== code) });
  };

  const addSacCode = (code) => {
    if (code && !formData.sac_codes.includes(code)) {
      setFormData({ ...formData, sac_codes: [...formData.sac_codes, code] });
    }
  };

  const removeSacCode = (code) => {
    setFormData({ ...formData, sac_codes: formData.sac_codes.filter(c => c !== code) });
  };

  // Filter and paginate data
  const getFilteredVendors = () => {
    let filtered = [...vendors];

    // Apply search filter (search by legal entity, trade name, POC email, GST number)
    if (searchTerm) {
      filtered = filtered.filter(vendor => 
        vendor.legal_entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.trade_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.poc_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.gst_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.poc_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vendor => vendor.vendor_status === statusFilter);
    }

    return filtered;
  };

  const filteredVendors = getFilteredVendors();
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentVendors = filteredVendors.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
            Vendor Management
          </h1>
          <p className="text-gray-600 mt-1">Manage vendor information and documents</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Vendor
        </Button>
      </div>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Vendors ({vendors.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filter and Search Controls */}
          <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 items-center flex-1">
              <input
                type="text"
                placeholder="Search by legal entity, trade name, POC, GST..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="active">Active</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Legal Entity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Trade Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">POC Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Created By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Updated By</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Edit</th>
                  {user?.role === 'admin' && (
                    <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Delete</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={user?.role === 'admin' ? "9" : "8"} className="px-4 py-8 text-center text-gray-500">
                      No vendors found
                    </td>
                  </tr>
                ) : (
                  currentVendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{vendor.legal_entity_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vendor.trade_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vendor.poc_email || '-'}</td>
                      <td className="px-4 py-3">{getStatusBadge(vendor.vendor_status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vendor.created_by || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vendor.updated_by || vendor.created_by || '-'}</td>
                      <td className="px-3 py-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetailsDialog(vendor)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(vendor)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-3 py-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDeleteDialog(vendor)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredVendors.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredVendors.length)} of {filteredVendors.length} results
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={currentPage === page ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                        >
                          {page}
                        </Button>
                      </div>
                    ))
                  }
                </div>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Vendor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Vendor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateVendor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Legal Entity Name *</Label>
                <Input
                  value={formData.legal_entity_name}
                  onChange={(e) => setFormData({ ...formData, legal_entity_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Trade Name</Label>
                <Input
                  value={formData.trade_name}
                  onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                />
              </div>
              <div>
                <Label>GST Registered *</Label>
                <select
                  value={formData.gst_registered}
                  onChange={(e) => setFormData({ ...formData, gst_registered: e.target.value, gst_number: e.target.value === 'no' ? '' : formData.gst_number })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              {formData.gst_registered === 'yes' && (
                <div>
                  <Label>GST Number *</Label>
                  <Input
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    required={formData.gst_registered === 'yes'}
                  />
                </div>
              )}
              <div>
                <Label>Credit Period (Days) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.credit_period}
                  onChange={(e) => setFormData({ ...formData, credit_period: e.target.value })}
                  placeholder="Enter credit period in days"
                  required
                />
              </div>
              <div>
                <Label>State *</Label>
                <div className="relative">
                  <Input
                    value={stateSearchTerm || formData.state}
                    onChange={(e) => setStateSearchTerm(e.target.value)}
                    onFocus={() => setStateSearchTerm('')}
                    placeholder="Search and select state..."
                    required
                  />
                  {stateSearchTerm !== '' && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {states
                        .filter(state => 
                          state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
                          state.code.toLowerCase().includes(stateSearchTerm.toLowerCase())
                        )
                        .map(state => (
                          <div
                            key={state.code}
                            onClick={() => {
                              setFormData({ ...formData, state: state.name });
                              setStateSearchTerm('');
                            }}
                            className="px-4 py-2 hover:bg-indigo-50 cursor-pointer"
                          >
                            {state.name}
                          </div>
                        ))}
                      {states.filter(state => 
                        state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
                        state.code.toLowerCase().includes(stateSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="px-4 py-2 text-gray-500">No states found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Registered Address *</Label>
                <Input
                  value={formData.registered_address}
                  onChange={(e) => setFormData({ ...formData, registered_address: e.target.value })}
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>Business Address *</Label>
                <Input
                  value={formData.business_address}
                  onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>POC Name *</Label>
                <Input
                  value={formData.poc_name}
                  onChange={(e) => setFormData({ ...formData, poc_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>POC Mobile</Label>
                <Input
                  type="tel"
                  pattern="[6-9][0-9]{9}"
                  maxLength="10"
                  value={formData.poc_mobile}
                  onChange={(e) => {
                    // Allow only numbers and limit to 10 digits
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    setFormData({ ...formData, poc_mobile: value });
                  }}
                  placeholder="10-digit mobile number (e.g., 9876543210)"
                />
                <p className="text-xs text-gray-500 mt-1">Optional - Must be a valid 10-digit Indian mobile number starting with 6-9</p>
              </div>
              <div className="col-span-2">
                <Label>POC Email</Label>
                <Input
                  type="email"
                  value={formData.poc_email}
                  onChange={(e) => setFormData({ ...formData, poc_email: e.target.value })}
                />
              </div>
            </div>

            {/* HSN Codes */}
            <div>
              <Label>HSN Codes</Label>
              <div className="flex gap-2 mb-2">
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  onChange={(e) => {
                    addHsnCode(e.target.value);
                    e.target.value = '';
                  }}
                >
                  <option value="">Select HSN Code</option>
                  {hsnCodes.map((code) => (
                    <option key={code.code} value={code.code}>
                      {code.code} - {code.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.hsn_codes.map((code) => (
                  <Badge key={code} className="bg-blue-100 text-blue-800">
                    {code}
                    <button type="button" onClick={() => removeHsnCode(code)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* SAC Codes */}
            <div>
              <Label>SAC Codes</Label>
              <div className="flex gap-2 mb-2">
                <select
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  onChange={(e) => {
                    addSacCode(e.target.value);
                    e.target.value = '';
                  }}
                >
                  <option value="">Select SAC Code</option>
                  {sacCodes.map((code) => (
                    <option key={code.code} value={code.code}>
                      {code.code} - {code.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.sac_codes.map((code) => (
                  <Badge key={code} className="bg-green-100 text-green-800">
                    {code}
                    <button type="button" onClick={() => removeSacCode(code)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Documents */}
            <div className="space-y-2">
              <Label>CIN Document</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocuments({ ...documents, cin: e.target.files[0] })}
              />
              <p className="text-xs text-gray-500">Optional - Accepted formats: PDF, JPG, PNG</p>
            </div>
            <div className="space-y-2">
              <Label>PAN Document *</Label>
              <Input
                type="file"
                required
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocuments({ ...documents, pan: e.target.files[0] })}
              />
              <p className="text-xs text-gray-500">Required - Accepted formats: PDF, JPG, PNG</p>
            </div>
            {formData.gst_registered === 'yes' && (
              <div className="space-y-2">
                <Label>GST Certificate *</Label>
                <Input
                  type="file"
                  required={formData.gst_registered === 'yes'}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setDocuments({ ...documents, gst: e.target.files[0] })}
                />
                <p className="text-xs text-gray-500">Required - Accepted formats: PDF, JPG, PNG</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Cancelled Cheque</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDocuments({ ...documents, cheque: e.target.files[0] })}
              />
              <p className="text-xs text-gray-500">Optional - Accepted formats: PDF, JPG, PNG</p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                {loading ? 'Creating...' : 'Create Vendor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      {selectedVendor && (
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>Vendor Details</span>
                {getStatusBadge(selectedVendor.vendor_status)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Legal Entity Name</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.legal_entity_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Trade Name</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.trade_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">GST Registered</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.gst_registered === 'yes' ? 'Yes' : 'No'}</p>
                </div>
                {selectedVendor.gst_registered === 'yes' && (
                  <div>
                    <p className="text-sm text-gray-600">GST Number</p>
                    <p className="text-sm font-medium mt-1">{selectedVendor.gst_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Credit Period</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.credit_period ? `${selectedVendor.credit_period} days` : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">State</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.state}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Registered Address</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.registered_address}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Business Address</p>
                  <p className="text-sm font-medium mt-1">{selectedVendor.business_address}</p>
                </div>
              </div>

              {/* POC Info */}
              <div>
                <h3 className="font-semibold mb-2">Point of Contact</h3>
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-sm font-medium mt-1">{selectedVendor.poc_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mobile</p>
                    <p className="text-sm font-medium mt-1">{selectedVendor.poc_mobile || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm font-medium mt-1">{selectedVendor.poc_email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Codes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">HSN Codes</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.hsn_codes && selectedVendor.hsn_codes.length > 0 ? (
                      selectedVendor.hsn_codes.map((code) => (
                        <Badge key={code} className="bg-blue-100 text-blue-800">{code}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No HSN codes</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">SAC Codes</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.sac_codes && selectedVendor.sac_codes.length > 0 ? (
                      selectedVendor.sac_codes.map((code) => (
                        <Badge key={code} className="bg-green-100 text-green-800">{code}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No SAC codes</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="font-semibold mb-3">Documents</h3>
                <div className="grid grid-cols-1 gap-2">
                  {selectedVendor.cin_document_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(selectedVendor.cin_document_url, `CIN_${selectedVendor.legal_entity_name}`)}
                      className="flex items-center gap-2 justify-start hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download CIN Document
                    </Button>
                  )}
                  
                  {selectedVendor.pan_document_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(selectedVendor.pan_document_url, `PAN_${selectedVendor.legal_entity_name}`)}
                      className="flex items-center gap-2 justify-start hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download PAN Document
                    </Button>
                  )}
                  
                  {selectedVendor.gst_certificate_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(selectedVendor.gst_certificate_url, `GST_${selectedVendor.legal_entity_name}`)}
                      className="flex items-center gap-2 justify-start hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download GST Certificate
                    </Button>
                  )}
                  
                  {selectedVendor.cheque_document_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(selectedVendor.cheque_document_url, `Cheque_${selectedVendor.legal_entity_name}`)}
                      className="flex items-center gap-2 justify-start hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Cancelled Cheque
                    </Button>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg text-sm">
                <div>
                  <p className="text-gray-600">Created By</p>
                  <p className="font-medium mt-1">{selectedVendor.created_by || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Created At</p>
                  <p className="font-medium mt-1">
                    {selectedVendor.created_at ? new Date(selectedVendor.created_at).toLocaleString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Updated By</p>
                  <p className="font-medium mt-1">{selectedVendor.updated_by || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Updated At</p>
                  <p className="font-medium mt-1">
                    {selectedVendor.updated_at ? new Date(selectedVendor.updated_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Vendor Dialog - Same as Add but with update handler */}
      {selectedVendor && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Vendor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateVendor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Legal Entity Name *</Label>
                  <Input
                    value={formData.legal_entity_name}
                    onChange={(e) => setFormData({ ...formData, legal_entity_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Trade Name</Label>
                  <Input
                    value={formData.trade_name}
                    onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>GST Registered *</Label>
                  <select
                    value={formData.gst_registered}
                    onChange={(e) => setFormData({ ...formData, gst_registered: e.target.value, gst_number: e.target.value === 'no' ? '' : formData.gst_number })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                {formData.gst_registered === 'yes' && (
                  <div>
                    <Label>GST Number *</Label>
                    <Input
                      value={formData.gst_number}
                      onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                      required={formData.gst_registered === 'yes'}
                    />
                  </div>
                )}
                <div>
                  <Label>Credit Period (Days) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.credit_period}
                    onChange={(e) => setFormData({ ...formData, credit_period: e.target.value })}
                    placeholder="Enter credit period in days"
                    required
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <div className="relative">
                    <Input
                      value={stateSearchTerm || formData.state}
                      onChange={(e) => setStateSearchTerm(e.target.value)}
                      onFocus={() => setStateSearchTerm('')}
                      placeholder="Search and select state..."
                      required
                    />
                    {stateSearchTerm !== '' && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {states
                          .filter(state => 
                            state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
                            state.code.toLowerCase().includes(stateSearchTerm.toLowerCase())
                          )
                          .map(state => (
                            <div
                              key={state.code}
                              onClick={() => {
                                setFormData({ ...formData, state: state.name });
                                setStateSearchTerm('');
                              }}
                              className="px-4 py-2 hover:bg-indigo-50 cursor-pointer"
                            >
                              {state.name}
                            </div>
                          ))}
                        {states.filter(state => 
                          state.name.toLowerCase().includes(stateSearchTerm.toLowerCase()) ||
                          state.code.toLowerCase().includes(stateSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-2 text-gray-500">No states found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Registered Address *</Label>
                  <Input
                    value={formData.registered_address}
                    onChange={(e) => setFormData({ ...formData, registered_address: e.target.value })}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Business Address *</Label>
                  <Input
                    value={formData.business_address}
                    onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>POC Name *</Label>
                  <Input
                    value={formData.poc_name}
                    onChange={(e) => setFormData({ ...formData, poc_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>POC Mobile</Label>
                  <Input
                    type="tel"
                    pattern="[6-9][0-9]{9}"
                    maxLength="10"
                    value={formData.poc_mobile}
                    onChange={(e) => {
                      // Allow only numbers and limit to 10 digits
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                      setFormData({ ...formData, poc_mobile: value });
                    }}
                    placeholder="10-digit mobile number (e.g., 9876543210)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional - Must be a valid 10-digit Indian mobile number starting with 6-9</p>
                </div>
                <div className="col-span-2">
                  <Label>POC Email</Label>
                  <Input
                    type="email"
                    value={formData.poc_email}
                    onChange={(e) => setFormData({ ...formData, poc_email: e.target.value })}
                  />
                </div>
              </div>

              {/* HSN Codes */}
              <div>
                <Label>HSN Codes</Label>
                <div className="flex gap-2 mb-2">
                  <select
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    onChange={(e) => {
                      addHsnCode(e.target.value);
                      e.target.value = '';
                    }}
                  >
                    <option value="">Select HSN Code</option>
                    {hsnCodes.map((code) => (
                      <option key={code.code} value={code.code}>
                        {code.code} - {code.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.hsn_codes.map((code) => (
                    <Badge key={code} className="bg-blue-100 text-blue-800">
                      {code}
                      <button type="button" onClick={() => removeHsnCode(code)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* SAC Codes */}
              <div>
                <Label>SAC Codes</Label>
                <div className="flex gap-2 mb-2">
                  <select
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    onChange={(e) => {
                      addSacCode(e.target.value);
                      e.target.value = '';
                    }}
                  >
                    <option value="">Select SAC Code</option>
                    {sacCodes.map((code) => (
                      <option key={code.code} value={code.code}>
                        {code.code} - {code.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.sac_codes.map((code) => (
                    <Badge key={code} className="bg-green-100 text-green-800">
                      {code}
                      <button type="button" onClick={() => removeSacCode(code)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-2">
                <Label>CIN Document</Label>
                {selectedVendor.cin_document_url && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                    <span className="text-sm text-green-700">✓ Current document available</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadFile(selectedVendor.cin_document_url, 'CIN_Document')}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setDocuments({ ...documents, cin: e.target.files[0] })}
                />
                <p className="text-xs text-gray-500">
                  {selectedVendor.cin_document_url ? 'Upload new file to replace existing document' : 'Optional - Upload CIN document'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>PAN Document *</Label>
                {selectedVendor.pan_document_url ? (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                    <span className="text-sm text-green-700">✓ Current document available</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadFile(selectedVendor.pan_document_url, 'PAN_Document')}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-red-600 mb-2">⚠ No document uploaded - Please upload</p>
                )}
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setDocuments({ ...documents, pan: e.target.files[0] })}
                  required={!selectedVendor.pan_document_url}
                />
                <p className="text-xs text-gray-500">
                  {selectedVendor.pan_document_url ? 'Upload new file to replace existing document' : 'Required - Upload PAN document'}
                </p>
              </div>
              {formData.gst_registered === 'yes' && (
                <div className="space-y-2">
                  <Label>GST Certificate *</Label>
                  {selectedVendor.gst_certificate_url ? (
                    <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                      <span className="text-sm text-green-700">✓ Current document available</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadFile(selectedVendor.gst_certificate_url, 'GST_Certificate')}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-red-600 mb-2">⚠ No document uploaded - Please upload</p>
                  )}
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setDocuments({ ...documents, gst: e.target.files[0] })}
                    required={!selectedVendor.gst_certificate_url}
                  />
                  <p className="text-xs text-gray-500">
                    {selectedVendor.gst_certificate_url ? 'Upload new file to replace existing document' : 'Required - Upload GST certificate'}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Cancelled Cheque</Label>
                {selectedVendor.cheque_document_url && (
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                    <span className="text-sm text-green-700">✓ Current document available</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadFile(selectedVendor.cheque_document_url, 'Cancelled_Cheque')}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setDocuments({ ...documents, cheque: e.target.files[0] })}
                />
                <p className="text-xs text-gray-500">
                  {selectedVendor.cheque_document_url ? 'Upload new file to replace existing document' : 'Optional - Upload cancelled cheque'}
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                  {loading ? 'Updating...' : 'Update Vendor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vendor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete vendor <strong>{vendorToDelete?.legal_entity_name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteVendor}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? 'Deleting...' : 'Delete Vendor'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorManagement;
